import { isToday } from "date-fns";
import { useCallback } from "react";
import { Pressable, StyleSheet, View } from "react-native";
import type { DateData } from "react-native-calendars";
import type { BasicDayProps } from "react-native-calendars/src/calendar/day/basic";
import { BackgroundGradient } from "~/components/layout/BackgroundGradient";
import { DAY_SIZE, Day } from "~/components/layout/calendar/Day";
import { MARKER_SIZE } from "~/components/layout/calendar/Markers";
import { Only } from "~/components/permission/Only";
import { QendoIcon } from "~/components/QendoIcon";
import { useTheme } from "~/providers/ThemeProvider";
import { filled } from "~/util";

type WeekDayItemProps = {
    date?: DateData;
} & Omit<BasicDayProps, "date">;

export const WEEK_DAY_GAP = 8;
export const WEEK_MARKER_WIDTH = 6;
export const WEEK_MARKER_HEIGHT = 82;
export const WEEK_DAY_ITEM_HEIGHT = DAY_SIZE + WEEK_DAY_GAP + WEEK_MARKER_HEIGHT;

export const WeekDayItem = ({ marking, date, onPress, onLongPress }: WeekDayItemProps) => {
    const { colours } = useTheme();
    const onDayPress = useCallback(() => onPress?.(date), [onPress, date]);
    const onDayLongPress = useCallback(() => onLongPress?.(date), [onLongPress, date]);
    const is_period = filled(marking?.periods?.length);
    const is_today = filled(date?.dateString) && isToday(date.dateString);

    return (
        <Pressable style={styles.container} onPress={onDayPress} onLongPress={onDayLongPress}>
            <Day
                day={date?.day}
                today={marking?.today}
                colour={is_today ? colours.text.secondary : colours.tracking.indicator}
                date={date?.dateString}
                period={is_period}
            />
            <Only when={marking?.marked}>
                <QendoIcon name="circle-lg-filled" size={MARKER_SIZE} color={colours.tracking.indicator} />
            </Only>
            <View style={[styles.upright, { backgroundColor: colours.tracking.unselected }]}>
                <Only when={marking?.today}>
                    <BackgroundGradient
                        colors={[colours.tracking["gradient-top"], colours.tracking["gradient-bottom"]]}
                        style={styles.upright}
                    />
                </Only>
            </View>
        </Pressable>
    );
};

const styles = StyleSheet.create({
    container: {
        justifyContent: "center",
        alignItems: "center",
        gap: WEEK_DAY_GAP,
        maxHeight: WEEK_DAY_ITEM_HEIGHT,
        flex: 1,
    },
    upright: {
        flex: 1,
        maxHeight: WEEK_MARKER_HEIGHT,
        width: WEEK_MARKER_WIDTH,
        borderTopLeftRadius: WEEK_MARKER_WIDTH,
        borderTopRightRadius: WEEK_MARKER_WIDTH,
        position: "relative",
    },
});
